import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Settings,
    Flame,
    RadioTower,
    Globe,
    Droplets,
    Calculator,
    Wind,
    Thermometer,
    TriangleAlert,
    FileEdit,
    ChevronRight,
    Info,
    Pin,
    Save,
    Gauge,
    Cpu,
    CircleGauge,
    Waves,
    Loader2,
    CheckCircle,
    XCircle
} from 'lucide-react';

// ============================================
// ✅ Вспомогательные функции форматирования
// ============================================
const getRejimRabot = (num) => {
    const map = {
        0: 'Отключен',
        1: 'Тепловычислитель',
        2: 'Поверка',
        3: 'Управление ИТП'
    };
    return map[num] || '—';
};

// ✅ Время интегрирования: в секундах для ИТП, в минутах для остальных
const getTimeIntegrirov = (num, workFunction) => {
    if (num === null || num === undefined) return '—';
    if (workFunction === 3) return `${num} сек`;
    return `${num} мин`;
};

const getActionsOnNS = (num) => {
    const map = { 0: 'Авария', 1: 'Исп. нормативные знач.' };
    return map[num] || '—';
};

const getActionsOnSwitchOff = (num) => {
    const map = { 0: 'Отключено', 1: 'Авария', 2: 'Уведомление' };
    return map[num] || '—';
};

const getCalibrovkaType = (num) => {
    const map = {
        0: 'Генерация отключена',
        1: 'Через измерение длительности импульса',
        2: 'Через изм. половины частоты часов. кварца'
    };
    return map[num] || '—';
};


const getOperatorFromNum = (num) => {
    const map = {
        0: 'Не назначен',
        1: 'МТС',
        2: 'МегаФон',
        3: 'Билайн',
        4: 'Теле2',
        5: 'Ростелеком'
    };
    return map[num] || '—';
};

const getTempDatchType = (num) => {
    const map = { 0: '—', 1: 'Платиновый, а=0,00385', 2: 'Платиновый, а=0,00391', 3: 'Медный', 4: 'Никелевый', 200: '—' };
    return map[num] || '—';
};

const getTempDatchRo = (num) => {
    const map = { 0: '—', 1: '50', 2: '100', 3: '500', 4: '1000' };
    return map[num] || '—';
};

const formatFloat = (val) => {
    if (val === null || val === undefined) return '—';
    return Number(val).toLocaleString('ru-RU', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

// ✅ Форматирование дат отопительного сезона (ISO формат: yyyy-MM-dd)
const formatDateShort = (dateStr) => {
    if (!dateStr) return 'Не назначено';

    // ✅ Если строка уже отформатирована (содержит русское название месяца)
    const russianMonths = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

    const isAlreadyFormatted = russianMonths.some(month =>
        dateStr.toLowerCase().includes(month)
    );

    if (isAlreadyFormatted) {
        return dateStr; // Возвращаем как есть
    }

    // ✅ Иначе парсим как ISO дату
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return 'Не назначено';

        const months = ['Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня',
            'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'];
        return `${d.getDate()} ${months[d.getMonth()]}`;
    } catch {
        return 'Не назначено';
    }
};

// ============================================
// ✅ Компонент строки таблицы настроек
// ============================================
const SettingsRow = ({ param, value, highlight = false }) => (
    <div className={`flex items-center px-4 py-2 border-b border-slate-700/50 hover:bg-slate-700/20 transition ${highlight ? 'bg-blue-500/5' : ''}`}>
        <div className="text-slate-300 text-sm flex-1">{param}</div>
        <div className="text-white text-sm font-medium min-w-[150px] text-right">{value}</div>
    </div>
);

// ============================================
// ✅ Подвкладка "Параметры"
// ============================================
const ParametersSubTab = ({ settings, isAdmin }) => {
    if (!settings) return <div className="text-slate-400 text-center py-8">Загрузка...</div>;

    const { parameters, connection } = settings;

    // ✅ Отопительный сезон
    const seasonText = (parameters.startHeatdate && parameters.endHeatdate)
        ? `С ${formatDateShort(parameters.startHeatdate)} по ${formatDateShort(parameters.endHeatdate)}`
        : 'Не назначено';

    return (
        <div className="space-y-4">
            {/* Тепловычислитель */}
            <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden">
                <div className="bg-slate-800 px-4 py-2 border-b border-slate-700 flex items-center justify-between">
                    <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Cpu className="w-5 h-5 text-cyan-400" />
                        Тепловычислитель
                    </h4>
                    {isAdmin && (
                        <button className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-medium transition">
                            Изменить
                        </button>
                    )}
                </div>
                <SettingsRow param="Режим работы" value={getRejimRabot(parameters.work_function)} />
                <SettingsRow
                    param="Время интегрирования"
                    value={getTimeIntegrirov(parameters.time_integrirov, parameters.work_function)}
                />
                <SettingsRow param="Действия при НС" value={getActionsOnNS(parameters.actionsOnNS)} />
                <SettingsRow param="Действие при откл. внешнего питания" value={getActionsOnSwitchOff(parameters.actionsOnSwitchOffPitanie)} />
                <SettingsRow param="Отопительный сезон" value={seasonText} />
                <SettingsRow param="Способ поверки" value={getCalibrovkaType(parameters.calibrovkaSetType)} />
                <SettingsRow param="Логин тепловычислителя" value={parameters.tv_login || '—'} />
                <SettingsRow param="Пароль" value="**********" />
            </div>

            {/* GSM передатчик */}
            <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden">
                <div className="bg-slate-800 px-4 py-2 border-b border-slate-700 flex items-center justify-between">
                    <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                        <RadioTower className="w-5 h-5 text-blue-400" />
                        GSM передатчик
                    </h4>
                    {isAdmin && (
                        <button className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-medium transition">
                            Изменить
                        </button>
                    )}
                </div>
                {connection ? (
                    <>
                        <SettingsRow param="GSM передатчик" value={connection.switch_GSM_modul === 0 ? 'Выключен' : 'Включен'} />
                        {connection.switch_GSM_modul !== 0 && (
                            <>
                                <SettingsRow param="Оператор сети" value={getOperatorFromNum(connection.gsm_operator)} />
                                <SettingsRow param="Точка доступа (APN)" value={connection.gsm_avp_point || '—'} />
                                <SettingsRow param="Номер телефона (без 8)" value={connection.gsm_number_tel || '—'} />
                                <SettingsRow param="Режим ожидания" value={connection.gsm_wait === 1 ? 'Включен' : 'Выключен'} />
                                <SettingsRow param="Логин" value={connection.gsm_login || '—'} />
                                <SettingsRow param="Пароль" value="**********" />
                            </>
                        )}
                    </>
                ) : (
                    <div className="px-4 py-3 text-slate-500 text-sm">Нет данных</div>
                )}
            </div>

            {/* Подключение к серверу */}
            <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden">
                <div className="bg-slate-800 px-4 py-2 border-b border-slate-700 flex items-center justify-between">
                    <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Globe className="w-5 h-5 text-green-400" />
                        Подключение к информационному центру (серверу)
                    </h4>
                    {isAdmin && (
                        <button className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-medium transition">
                            Изменить
                        </button>
                    )}
                </div>
                {connection ? (
                    <>
                        <SettingsRow param="Тип подключения" value={connection.inf_switch_domen_ip === 0 ? 'IP' : 'Домен'} />
                        <SettingsRow param="IP/домен" value={connection.inf_ip || '—'} />
                        <SettingsRow param="Порт сервера" value={connection.inf_port || '—'} />
                        <SettingsRow param="Логин" value={connection.inf_login || '—'} />
                        <SettingsRow param="Пароль" value="**********" />
                    </>
                ) : (
                    <div className="px-4 py-3 text-slate-500 text-sm">Нет данных</div>
                )}
            </div>
        </div>
    );
};

// ============================================
// ✅ Подвкладка "Алгоритм учета"
// ============================================
const AlgoritmSubTab = ({ settings, isAdmin }) => {
    if (!settings) return <div className="text-slate-400 text-center py-8">Загрузка...</div>;

    const { algoritm, coldWater } = settings;

    // ✅ Логика параметров холодной воды по WinForms
    const getTempColdWaterRow = () => {
        const switchVal = coldWater.switch_TempColdWater;
        const sisSI = coldWater.sis_count_SI;
        const sisFT = coldWater.sis_count_FT;

        if (switchVal === 0) {
            // Отключен или Не используется в зависимости от схемы
            if (((sisSI === 2) && (sisFT === 1)) || ((sisSI === 2) && (sisFT === 2)) ||
                sisSI === 8 || sisSI === 9 || sisSI === 10) {
                return { status: 'Отключен', variable: '—', summer: '—', winter: '—' };
            } else {
                return { status: 'Не используется', variable: '—', summer: '—', winter: '—' };
            }
        }
        if (switchVal === 1) {
            return {
                status: '= t4',
                variable: '—',
                summer: formatFloat(coldWater.txv_summer),
                winter: formatFloat(coldWater.txv_winter)
            };
        }
        if (switchVal === 2) {
            return {
                status: 'Задан вручную',
                variable: '—',
                summer: formatFloat(coldWater.txv_summer),
                winter: formatFloat(coldWater.txv_winter)
            };
        }
        return { status: '—', variable: '—', summer: '—', winter: '—' };
    };

    const getPressureColdWaterRow = () => {
        const switchVal = coldWater.switch_DavlenieColdWater;
        const sisSI = coldWater.sis_count_SI;
        const sisFT = coldWater.sis_count_FT;

        if (switchVal === 0) {
            if (((sisSI === 2) && (sisFT === 1)) || ((sisSI === 2) && (sisFT === 2)) ||
                sisSI === 8 || sisSI === 9 || sisSI === 10) {
                return { status: 'Отключен', variable: '—', summer: '—', winter: '—' };
            } else {
                return { status: 'Не используется', variable: '—', summer: '—', winter: '—' };
            }
        }
        if (switchVal === 1) {
            return {
                status: '= P4',
                variable: '—',
                summer: formatFloat(coldWater.pxv_summer),
                winter: formatFloat(coldWater.pxv_winter)
            };
        }
        if (switchVal === 2) {
            return {
                status: 'Задан вручную',
                variable: '—',
                summer: formatFloat(coldWater.pxv_summer),
                winter: formatFloat(coldWater.pxv_winter)
            };
        }
        return { status: '—', variable: '—', summer: '—', winter: '—' };
    };

    const getVolumeColdWaterRow = () => {
        const switchVal = coldWater.switch_RasxodColdWater;
        const sisSI = coldWater.sis_count_SI;
        const sisFT = coldWater.sis_count_FT;

        // Не используется для определенных схем
        if (sisSI === 0 ||
            ((sisSI === 14) && ((sisFT === 0) || (sisFT === 1))) ||
            sisSI === 15 || sisSI === 20) {
            return { status: 'Не используется', variable: '—' };
        }

        if (switchVal === 0) {
            return { status: 'Отключен', variable: '—' };
        }
        if (switchVal === 1) {
            return { status: '= V4', variable: '—' };
        }
        return { status: '—', variable: '—' };
    };

    const txRow = getTempColdWaterRow();
    const pxRow = getPressureColdWaterRow();
    const vxRow = getVolumeColdWaterRow();

    return (
        <div className="space-y-4">
            {/* Алгоритм учета ТЭ */}
            <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden">
                <div className="bg-slate-800 px-4 py-2 border-b border-slate-700 flex items-center justify-between">
                    <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-purple-400" />
                        Алгоритм учета тепловой энергии
                    </h4>
                </div>
                <SettingsRow param="Схема измерений (СИ)" value={`№ ${algoritm.sis_count_SI}`} />
                <SettingsRow param="Формулы расчета (ФТ)" value={`№ ${algoritm.sis_count_FT}`} />
            </div>

            {/* Параметры холодной воды */}
            <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden">
                <div className="bg-slate-800 px-4 py-2 border-b border-slate-700 flex items-center justify-between">
                    <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Droplets className="w-5 h-5 text-cyan-400" />
                        Параметры холодной воды
                    </h4>
                    {isAdmin && (
                        <button className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-medium transition">
                            Изменить
                        </button>
                    )}
                </div>
                <div className="overflow-x-auto custom-scrollbar-x">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-800/50">
                            <tr>
                                <th className="text-left text-slate-400 font-semibold px-3 py-2 w-16"></th>
                                <th className="text-left text-slate-400 font-semibold px-3 py-2">Параметр</th>
                                <th className="text-center text-slate-400 font-semibold px-3 py-2">Летнее время</th>
                                <th className="text-center text-slate-400 font-semibold px-3 py-2">Зимнее время</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            <tr className="hover:bg-slate-700/20">
                                <td className="px-3 py-2 text-slate-500">tx</td>
                                <td className="px-3 py-2 text-white">{txRow.status}</td>
                                <td className="px-3 py-2 text-center text-white font-mono">{txRow.summer}</td>
                                <td className="px-3 py-2 text-center text-white font-mono">{txRow.winter}</td>
                            </tr>
                            <tr className="hover:bg-slate-700/20">
                                <td className="px-3 py-2 text-slate-500">Px</td>
                                <td className="px-3 py-2 text-white">{pxRow.status}</td>
                                <td className="px-3 py-2 text-center text-white font-mono">{pxRow.summer}</td>
                                <td className="px-3 py-2 text-center text-white font-mono">{pxRow.winter}</td>
                            </tr>
                            <tr className="hover:bg-slate-700/20">
                                <td className="px-3 py-2 text-slate-500">Vx</td>
                                <td className="px-3 py-2 text-white">{vxRow.status}</td>
                                <td className="px-3 py-2 text-center text-slate-500">—</td>
                                <td className="px-3 py-2 text-center text-slate-500">—</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ============================================
// ✅ Подвкладка "Датчики"
// ============================================
const SensorsSubTab = ({ settings, isAdmin }) => {
    if (!settings) return <div className="text-slate-400 text-center py-8">Загрузка...</div>;

    const { sensors } = settings;

    const filteredPressure = sensors.pressure.filter(s => s.switchVal !== 0);
    const filteredTemperature = sensors.temperature
        .map((s, idx) => ({ ...s, originalIdx: idx }))
        .filter(s => s.switchVal !== 0);
    const filteredVolume = sensors.volume.filter(s => s.switchVal !== 0);

    return (
        <div className="space-y-4">
            {/* Датчики температуры */}
            <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden">
                <div className="bg-slate-800 px-4 py-2 border-b border-slate-700 flex items-center justify-between">
                    <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Thermometer className="w-5 h-5 text-red-400" />
                        Температуры
                    </h4>
                    {isAdmin && (
                        <button className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-medium transition">
                            Изменить
                        </button>
                    )}
                </div>
                <div className="overflow-x-auto custom-scrollbar-x">
                    {filteredTemperature.length === 0 ? (
                        <div className="px-4 py-8 text-center text-slate-500 text-sm">
                            Нет активных датчиков температуры
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-800/50">
                                <tr>
                                    <th className="text-left text-slate-400 font-semibold px-3 py-2">Переменная</th>
                                    <th className="text-left text-slate-400 font-semibold px-3 py-2">Параметр</th>
                                    <th className="text-left text-slate-400 font-semibold px-3 py-2">Тип</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Ном. сопрот., Ro, Ом</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Температура, t, °C</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Мин темп., tmin, °C</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Макс темп., tmax, °C</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/50">
                                {filteredTemperature.map((s) => {
                                    const idx = s.originalIdx;
                                    const isAuto = s.switchVal === 1;
                                    const isManual = s.switchVal === 2;

                                    let paramText = 'Автоматически';
                                    if (isManual) paramText = 'Задан вручную';

                                    return (
                                        <tr key={s.index} className="hover:bg-slate-700/20">
                                            <td className="px-3 py-2 text-white font-medium">{s.name}</td>
                                            <td className="px-3 py-2 text-slate-300">{paramText}</td>
                                            <td className="px-3 py-2 text-slate-300">
                                                {isManual ? '—' : getTempDatchType(s.typeVal)}
                                            </td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {isManual ? '—' : getTempDatchRo(s.roVal)}
                                            </td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {!isManual ? '—' : formatFloat(s.tempValue)}
                                            </td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {!isAuto || idx === 8 ? '—' : formatFloat(s.tempMin)}
                                            </td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {!isAuto || idx === 8 ? '—' : formatFloat(s.tempMax)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Датчики давления */}
            <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden">
                <div className="bg-slate-800 px-4 py-2 border-b border-slate-700 flex items-center justify-between">
                    <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                        <CircleGauge className="w-5 h-5 text-cyan-400" />
                        Давления
                    </h4>
                    {isAdmin && (
                        <button className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-medium transition">
                            Изменить
                        </button>
                    )}
                </div>
                <div className="overflow-x-auto custom-scrollbar-x">
                    {filteredPressure.length === 0 ? (
                        <div className="px-4 py-8 text-center text-slate-500 text-sm">
                            Нет активных датчиков давления
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-800/50">
                                <tr>
                                    <th className="text-left text-slate-400 font-semibold px-3 py-2">Переменная</th>
                                    <th className="text-left text-slate-400 font-semibold px-3 py-2">Параметр</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Макс. дав., Pmax, МПа</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Знач. тока при P=0, Io, мА</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Знач. тока при P=Pmax, Imax, мА</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Давление, P, МПа</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Давление при НС, Pнс, МПа</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/50">
                                {filteredPressure.map((s) => {
                                    const isAuto = s.switchVal === 1;
                                    const isManual = s.switchVal === 2;

                                    let paramText = 'Автоматически';
                                    if (isManual) paramText = 'Задан вручную';

                                    return (
                                        <tr key={s.index} className="hover:bg-slate-700/20">
                                            <td className="px-3 py-2 text-white font-medium">{s.name}</td>
                                            <td className="px-3 py-2 text-slate-300">{paramText}</td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {isManual ? '—' : formatFloat(s.pMax)}
                                            </td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {isManual ? '—' : formatFloat(s.io)}
                                            </td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {isManual ? '—' : formatFloat(s.imax)}
                                            </td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {!isManual ? '—' : formatFloat(s.value)}
                                            </td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {isManual ? '—' : formatFloat(s.valueOnNS)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Датчики объёмного расхода */}
            <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden">
                <div className="bg-slate-800 px-4 py-2 border-b border-slate-700 flex items-center justify-between">
                    <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Waves className="w-5 h-5 text-blue-400" />
                        Объёмного расхода
                    </h4>
                    {isAdmin && (
                        <button className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-medium transition">
                            Изменить
                        </button>
                    )}
                </div>
                <div className="overflow-x-auto custom-scrollbar-x">
                    {filteredVolume.length === 0 ? (
                        <div className="px-4 py-8 text-center text-slate-500 text-sm">
                            Нет активных датчиков объёмного расхода
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-800/50">
                                <tr>
                                    <th className="text-left text-slate-400 font-semibold px-3 py-2">Переменная</th>
                                    <th className="text-left text-slate-400 font-semibold px-3 py-2">Параметр</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Вес импульса, В, дм³/имп</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Мин расход, Vmin, м³/ч</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Переходный расход, Vper, м³/ч</th>
                                    <th className="text-center text-slate-400 font-semibold px-3 py-2">Макс расход, Vmax, м³/ч</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/50">
                                {filteredVolume.map((s) => {
                                    return (
                                        <tr key={s.index} className="hover:bg-slate-700/20">
                                            <td className="px-3 py-2 text-white font-medium">{s.name}</td>
                                            <td className="px-3 py-2 text-slate-300">Подключен</td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {formatFloat(s.bimpulsa)}
                                            </td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {formatFloat(s.minValue)}
                                            </td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {formatFloat(s.perValue)}
                                            </td>
                                            <td className="px-3 py-2 text-center text-white font-mono">
                                                {formatFloat(s.maxValue)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================
// ✅ Главный компонент вкладки "Настройки"
// ============================================
const SettingsTab = ({ device }) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const isAdmin = userInfo.levelRight >= 1;
    const canChangeComments = userInfo.canChangeComments === 1;

    const [activeSubTab, setActiveSubTab] = useState('parameters');
    const [settings, setSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // ✅ Состояния для замечаний
    const [statusText, setStatusText] = useState('');
    const [originalText, setOriginalText] = useState('');
    const [saveStatus, setSaveStatus] = useState('idle');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        loadSettings();
    }, [device.name]);

    // ✅ Инициализация замечаний при загрузке устройства
    useEffect(() => {
        const initialText = device?.statusDescription || '';
        setStatusText(initialText);
        setOriginalText(initialText);
    }, [device?.statusDescription]);

    const hasChanges = statusText !== originalText;

    const loadSettings = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            const response = await axios.get(
                `${API_URL}/api/devices/aksioma01/${device.name}/settings`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            console.log('⚙️ Settings:', response.data);
            setSettings(response.data);
        } catch (err) {
            console.error('Ошибка загрузки настроек:', err);
            setError('Не удалось загрузить настройки');
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ Обработчик сохранения замечаний
    const handleSaveStatus = async () => {
        if (!canChangeComments || !device?.name) return;

        setSaveStatus('saving');
        setErrorMessage('');

        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';

            const response = await axios.put(
                `${API_URL}/api/devices/aksioma01/${device.name}/status`,
                { statusDescription: statusText },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            console.log('✅ Замечания сохранены:', response.data);
            setSaveStatus('success');
            setOriginalText(statusText);
            setTimeout(() => setSaveStatus('idle'), 2500);
        } catch (err) {
            console.error('❌ Ошибка сохранения замечаний:', err);
            setSaveStatus('error');
            setErrorMessage(
                err.response?.data?.message ||
                err.message ||
                'Не удалось сохранить замечания'
            );
        }
    };

    const subTabs = [
        { id: 'parameters', name: 'Параметры', icon: Settings },
        { id: 'algoritm', name: 'Алгоритм учета', icon: Calculator },
        { id: 'sensors', name: 'Датчики', icon: Gauge },
    ];

    return (
        <div className="space-y-4">
            {/* Заголовок + предупреждение о правах */}
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                        <Settings className="w-5 h-5 text-slate-300" />
                        Настройки прибора
                    </h3>
                </div>


                {/* Подвкладки */}
                <div className="flex gap-1 mt-3 border-b border-slate-700">
                    {subTabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveSubTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition border-b-2 ${activeSubTab === tab.id
                                    ? 'border-blue-500 text-blue-300'
                                    : 'border-transparent text-slate-400 hover:text-white'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                <span>{tab.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Контент подвкладки */}
            {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                    <Loader2 className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">Загрузка настроек...</p>
                </div>
            ) : (
                <>
                    {activeSubTab === 'parameters' && <ParametersSubTab settings={settings} isAdmin={isAdmin} />}
                    {activeSubTab === 'algoritm' && <AlgoritmSubTab settings={settings} isAdmin={isAdmin} />}
                    {activeSubTab === 'sensors' && <SensorsSubTab settings={settings} isAdmin={isAdmin} />}
                </>
            )}

            {/* ============================================ */}
            {/* ✅ Замечания — с рабочей кнопкой сохранения */}
            {/* ============================================ */}
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                    <FileEdit className="w-5 h-5 text-slate-300" />
                    Замечания
                </h4>
                <div className="space-y-3">
                    <div>
                        <label className="block text-slate-400 text-xs mb-1 flex items-center gap-2">
                            <span>Статус устройства</span>
                            {hasChanges && (
                                <span className="text-yellow-400 text-xs flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse"></span>
                                    не сохранено
                                </span>
                            )}
                        </label>
                        <textarea
                            value={statusText}
                            onChange={(e) => setStatusText(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed resize-y"
                            placeholder="Введите замечания..."
                            readOnly={!canChangeComments}
                            disabled={saveStatus === 'saving'}
                        />
                    </div>

                    {/* ✅ Сообщение об успехе */}
                    {saveStatus === 'success' && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg text-green-300 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            <span>Замечания успешно сохранены</span>
                        </div>
                    )}

                    {/* ✅ Сообщение об ошибке */}
                    {saveStatus === 'error' && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
                            <XCircle className="w-4 h-4 flex-shrink-0" />
                            <span>{errorMessage}</span>
                        </div>
                    )}

                    {canChangeComments && (
                        <button
                            onClick={handleSaveStatus}
                            disabled={saveStatus === 'saving' || !hasChanges}
                            className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saveStatus === 'saving' ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Сохранение...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Сохранить замечания
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SettingsTab;